import { ToggleButton, ToggleButtonGroup, Box, Typography } from "@mui/material";
import { FC } from "react";
import { Option } from "types/option";

type SingleChoiceSelectorProps = {
  value?: number | null;
  selectedValues?: number[];
  onChange?: (value: number) => void;
  onMultipleChange?: (values: number[]) => void;
  options: Option[];
  multiple?: boolean;
};

export const SingleChoiceSelector: FC<SingleChoiceSelectorProps> = ({
  value,
  selectedValues = [],
  onChange,
  onMultipleChange,
  options,
  multiple = false,
}) => {
  const sortedOptions = [...options].sort((a, b) => (a.order || 0) - (b.order || 0));

  const handleChange = (_: React.MouseEvent<HTMLElement>, newValue: number | number[] | null) => {
    if (multiple) {
      // For multiple choice, ToggleButtonGroup returns an array when not exclusive
      if (Array.isArray(newValue)) {
        onMultipleChange?.(newValue);
      } else if (newValue !== null) {
        // Toggle the clicked option manually
        const currentValues = selectedValues || [];
        if (currentValues.includes(newValue)) {
          onMultipleChange?.(currentValues.filter((v) => v !== newValue));
        } else {
          onMultipleChange?.([...currentValues, newValue]);
        }
      }
    } else {
      // For single choice
      if (typeof newValue === "number") {
        onChange?.(newValue);
      }
    }
  };

  const currentValue = multiple 
    ? (selectedValues || []) 
    : (value !== undefined && value !== null ? value : null);

  return (
    <Box>
      <ToggleButtonGroup
        color="primary"
        value={currentValue}
        exclusive={!multiple}
        onChange={handleChange}
        orientation="vertical"
        fullWidth
        sx={{ 
          mt: 1,
          "& .MuiToggleButton-root": {
            py: 1.5,
            mb: 1,
            "&:last-child": {
              mb: 0,
            },
            "&.Mui-selected": {
              backgroundColor: "primary.main",
              color: "white",
              fontWeight: 600,
              "&:hover": {
                backgroundColor: "primary.main",
              },
            },
          },
        }}
      >
        {sortedOptions.map((opt, index) => (
          <ToggleButton value={index} key={opt.option_id || index}>
            <Typography variant="body1" sx={{ textTransform: "none" }}>
              {opt.text}
            </Typography>
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
    </Box>
  );
};
